// Percorso file: src/utils/playFootball.js
//
// IDONEITÀ AL PROGRAMMA «PLAY FOOTBALL» — DEFINIZIONE UNICA.
// (diario 8/8/2026, apertura U1-F, decisione 3)
//
// Play Football è la registrazione federale dei tesserati. La domanda
// «chi è idoneo?» ha UNA sola risposta in tutta l'app, e vive qui:
//
//     PLAYER o COACH, per ruolo o per context attivo,
//     fra i membri ACTIVE.
//
// I due pezzi della definizione stanno in due posti diversi di proposito:
//   - «PLAYER o COACH, per ruolo o per context attivo» è questa funzione;
//   - «fra i membri ACTIVE» resta del CHIAMANTE.
// Perché: le card di UsersList contano su una popolazione già filtrata
// (`statsUsers`), le spunte PF di riga lavorano su righe già scelte dal
// filtro della tabella. Se il filtro sullo status stesse qui dentro, la
// funzione servirebbe solo alle card e le spunte non potrebbero riusarla —
// cioè rinascerebbe la seconda definizione che questa fetta chiude.
//
// ⚠️ PERCHÉ UN FILE DEDICATO, e non `auth/access.js`.
// Questa NON è una domanda di permesso: non decide chi può fare qualcosa,
// descrive chi rientra in un programma sportivo. `auth/access.js` dichiara
// in testa di essere la sede unica di cinque cose (vocabolario, ruoli
// posseduti, permessi, cappello attuale, mappa delle rotte) e nient'altro.
// Una regola di prodotto lì dentro snaturerebbe il modulo d'accesso.
//
// ⚠️ NESSUN GATE NUOVO. La logica non si riscrive: è `hasRole`, cioè
// `canAccess` di `auth/access.js` (audit 08 §0.6: di gate diversi ne
// esistevano undici, non se ne aggiunge un dodicesimo). Il vocabolario dei
// ruoli di P0 arriva gratis: un documento storico "Child" vale PLAYER,
// "Manager"/"TD"/"ManagerTD" valgono COACH.
//
// COSA HA SOSTITUITO (difetto U1-A, doppia idoneità: rapporto 11 §8.1).
// Prima della fetta P2 la stessa domanda aveva QUATTRO risposte diverse:
//   - UsersList.jsx     ["Player","Coach","Manager","TD"]      → spunte di riga
//   - UsersList.jsx     ["Player","Coach","Manager"] + contexts → card
//   - Dashboard.jsx     idem, copia letterale                   → card
//   - PaymentsTable.jsx ["Player","Coach","Manager"]          → spunte
// P2 ritira Dashboard e PaymentsTable e porta le card qui; **P3** ha
// portato di qui anche le spunte di riga di UsersList: `isPFEligible` non
// esiste più, la cella PF della tabella chiama questa funzione.
//
// ⚠️ UN RAMO CADE, e va detto perché sposta numeri. Le due versioni da
// card consideravano idoneo anche chi avesse un context attivo con
// `team === "TEAMTD"`, QUALUNQUE fosse il suo ruolo. Qui conta il ruolo:
// un context TEAMTD è idoneo perché il suo ruolo è COACH (o Manager/TD/
// ManagerTD, che il vocabolario traduce), come pretende il modello di
// TeamAssignment. Un TEAMTD con ruolo
// diverso — un ADMIN, o un ruolo vuoto — prima contava e ora no.
// La decisione 3 nomina i ruoli, non il campo `team`: è voluto.
//
// ⚠️ CARD E SPUNTE CONCORDANO SUL CRITERIO, da P3 — lo stato intermedio
// dichiarato in P2 (card con denominatore pieno e righe tutte a «—» sui
// dati scritti in maiuscolo) è chiuso.
// NON concordano invece sulla POPOLAZIONE, ed è voluto: la card conta i
// soli ACTIVE (`statsUsers`, decisione 4 dell'8/8/2026), la tabella mostra
// ACTIVE + PENDING. Un giocatore in attesa ha le spunte in riga e non
// entra nel denominatore della card.
//
// ⚠️ SOLA LETTURA, come tutto il vocabolario dei ruoli: questa funzione
// non deve mai stare a monte di un salvataggio o di un payload.

import { hasRole } from "./roleUtils";

/**
 * Vero se la persona è idonea al programma Play Football.
 *
 * Un ruolo è «posseduto» se coincide con `user.role` di primo livello
 * OPPURE con il `role` di un context ACTIVE (gate unico, diario 5/8/2026),
 * su entrambi i lati in forma canonica.
 *
 * ⚠️ Non filtra sullo status dell'utente: quello è del chiamante (vedi
 * sopra). Chiedere l'idoneità di un ARCHIVED risponde comunque sul ruolo.
 *
 * @param {object|null} user documento utente (con eventuale array `contexts`)
 * @returns {boolean}
 */
export const isPlayFootballEligible = (user) => hasRole(user, "PLAYER", "COACH");
